import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { displayToastError } from '@/helpers';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { useFeatureFlags } from '@/modules/common/hooks/use-feature-flags';
import {
  invalidateInvoiceQueries,
  invalidateOverheadsQueries,
} from '@/modules/common/utils/query-invalidation';
import { AuthenticatedRoutes } from '@/router/routes';

import { createInvoices } from '../../api';
import { INVOICES_FILTER_KEY_BILLING_PERIOD } from '../../constants/invoices-filter-keys';
import { mapGenerateInvoicesToRequest } from '../../mappers';
import { GenerateInvoiceListItem } from '../../types';

interface GenerateInvoicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  billingPeriodLabel: string;
  billingPeriod: string;
  invoiceProjects: GenerateInvoiceListItem[];
  onSuccess?: () => void;
}

// Return to the list with only the generated period in the URL. Since the persisted-filters
// hook skips restoring localStorage when an owned key is present, this resets every other
// filter so the newly-created invoices (which fall in this period) are visible.
const buildInvoicesListUrl = (billingPeriod: string): string => {
  if (!billingPeriod) return AuthenticatedRoutes.Invoices;
  const params = new URLSearchParams({ [INVOICES_FILTER_KEY_BILLING_PERIOD]: billingPeriod });
  return `${AuthenticatedRoutes.Invoices}?${params.toString()}`;
};

export const GenerateInvoicesModal = ({
  onClose,
  isOpen,
  billingPeriodLabel,
  billingPeriod,
  invoiceProjects,
  onSuccess,
}: GenerateInvoicesModalProps) => {
  const { t } = useTranslation('authenticated.invoices', { keyPrefix: 'modals.generateInvoice' });
  const navigate = useNavigate();
  const { showPaymentDetails } = useFeatureFlags();

  const paymentDetailsAttachedCount = invoiceProjects.filter(
    project => project.paymentDetailsId != null
  ).length;

  const queryClient = useQueryClient();
  const { getErrorMessage } = useApiError();

  const { mutate, isPending } = useMutation({
    mutationFn: createInvoices,
    onError: (error: unknown) => {
      displayToastError(getErrorMessage(error, true));
    },
    onSuccess: () => {
      // Reset form before navigation to prevent unsaved changes guard from triggering
      onSuccess?.();
      void invalidateInvoiceQueries(queryClient);
      // Invoice generation flips scheduled income overheads to Invoiced.
      void invalidateOverheadsQueries(queryClient);
      navigate(buildInvoicesListUrl(billingPeriod));
      onClose();
    },
  });

  const handleConfirm = () => {
    // Map the invoice projects to API request format
    const invoicesRequest = mapGenerateInvoicesToRequest(invoiceProjects, billingPeriod);

    mutate(invoicesRequest);
  };

  return (
    <ConfirmationModal
      title={t('title')}
      hideCloseIcon
      message={
        <div>
          <Trans
            t={t}
            i18nKey="description"
            values={{ billingPeriod: billingPeriodLabel }}
            count={billingPeriodLabel ? 1 : 0}
          />
          {showPaymentDetails && paymentDetailsAttachedCount > 0 && (
            <p className="mt-2 text-sm text-muted-foreground">
              {t('paymentDetailsSummary', {
                attached: paymentDetailsAttachedCount,
                total: invoiceProjects.length,
              })}
            </p>
          )}
          <Alert variant={'warning'} className="mt-2">
            <AlertTriangle className="h-5 w-5" />
            <AlertDescription>{t('warning')}</AlertDescription>
          </Alert>
        </div>
      }
      confirmText={t('submit')}
      confirmVariant="default"
      open={isOpen}
      isLoading={isPending}
      onClose={onClose}
      onConfirm={handleConfirm}
    />
  );
};
